import React, { CSSProperties } from "react";
import { StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { createEditor, Descendant } from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";
import { useEventListener } from "usehooks-ts";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useIsMobile } from "src/hooks/ui";

import {
  ComposerContext,
  convertTextToDescendents,
  defaultInitialValue,
  insertLink,
  isEditorEmpty,
  isWeb,
  renderElement,
  renderLeaf,
  resetEditor,
  resetNodes,
  useComposer,
  withHtml,
} from "./utils";
import MessageFile from "./MessageFile";
import { tComposerFile, tComposerProps } from "./types";
import { useAddLinkModal } from "./hooks";
import { PdClose } from "src/components/Icons";
import { PdText } from "src/components/layout";

const MessageFiles: React.FC = () => {
  const { files, deleteMessageFile } = useComposer();

  if ((files?.length ?? 0) === 0) return null;

  return (
    <View style={[styles.flexRow, styles.flexWrap, styles.alignCenter, styles.px1]}>
      {files?.map((file, index) => (
        <View key={file.url} style={styles.m1}>
          <TouchableOpacity onPress={() => deleteMessageFile?.(index)} style={styles.removeButton}>
            <PdClose style={styles.removeButtonIcon} color={kColors.brandDark} />
          </TouchableOpacity>
          <MessageFile data={file} style={styles.messageFile} />
        </View>
      ))}
    </View>
  );
};

const Composer = React.forwardRef<TextInput, React.PropsWithChildren<tComposerProps>>((props, ref) => {
  const { from, to, onSend, value, onChangeText, children, ...rest } = props;
  const isMobile = useIsMobile();
  const toRef = React.useRef(to);
  const initialValue: Descendant[] = React.useMemo(
    () => (value ? convertTextToDescendents(value) || defaultInitialValue : defaultInitialValue),
    [value],
  );
  // const forceUpdate = useForceUpdate();
  const [editor] = React.useState(() => withHtml(withHistory(withReact(createEditor()))));
  const [files, setFiles] = React.useState<tComposerFile[]>([]);
  const addLinkModal = useAddLinkModal();

  const isEmpty = isEditorEmpty(editor);
  const canSendWeb = isWeb && !isEmpty;
  const canSendMobile = !isWeb && !!value?.trim();
  const canSend = canSendWeb || canSendMobile || (files?.length ?? 0) > 0;

  const addMessageFile = (file: tComposerFile) => {
    setFiles((prev) => [...prev, file]);
  };

  const deleteMessageFile = (index: number) => {
    setFiles((prev) => prev.filter((_prev, _index) => _index !== index));
  };

  const handleOnChangeEditable = (_value: Descendant[]) => {
    const content = JSON.stringify(_value);
    onChangeText?.(content);
  };

  const handleOnSend = () => {
    if (value && canSend) {
      onSend(value, files);
      if (files.length > 0) setFiles([]);
      if (isWeb) resetEditor(editor);
    }
  };

  const handleInsertLink = async () => {
    const response = await addLinkModal.openAsync();
    if (response.status === "closed") return;
    insertLink(editor, response.data.text, response.data.url);
  };

  useEventListener("keypress", (e: Event) => {
    if (e instanceof KeyboardEvent && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleOnSend();
    }
  });

  useEventListener("keydown", (e: Event) => {
    if (e instanceof KeyboardEvent && e.key === "k" && e.metaKey) {
      e.preventDefault();
      handleInsertLink();
    }
  });

  React.useEffect(() => {
    if (to !== toRef.current) {
      toRef.current = to;
      resetNodes(editor, { nodes: value ? convertTextToDescendents(value) : defaultInitialValue });
    }
  }, [editor, to, value]);

  return (
    <ComposerContext.Provider
      value={{
        value,
        files,
        canSend,
        onSend: handleOnSend,
        onAddLink: handleInsertLink,
        addMessageFile,
        deleteMessageFile,
        from,
        to,
      }}
    >
      <View style={styles.composerContainer}>
        <View style={isMobile ? styles.mobileComposer : styles.composer}>
          {isWeb ? (
            <Slate editor={editor} value={initialValue} onChange={handleOnChangeEditable}>
              <Editable
                style={webStyles.editable}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                renderPlaceholder={({ children: placeholderChildren, attributes }) => {
                  return (
                    // style necessary to prevent glitch on mobile devices
                    // eslint-disable-next-line react-native/no-inline-styles
                    <div {...attributes} style={{ ...attributes.style, top: 0 }}>
                      <PdText>{placeholderChildren}</PdText>
                    </div>
                  );
                }}
                placeholder={`Message ${to}`}
              />
              <MessageFiles />
              {children}
            </Slate>
          ) : (
            <>
              <TextInput
                ref={ref}
                multiline
                style={[styles.p2, styles.composerTextInput]}
                value={value}
                onChangeText={onChangeText}
                placeholder={`Message ${to}`}
                {...rest}
              />
              <MessageFiles />
              {children}
            </>
          )}
        </View>
        {!isEmpty && (
          <PdText style={[isMobile ? styles.mx5 : styles.mx10, styles.textXxs, styles.helperText, styles.colorGray]}>
            <PdText style={[styles.textXxs, styles.textMedium, styles.colorGray]}>Shift + Enter</PdText> to create a new
            line
          </PdText>
        )}
      </View>
    </ComposerContext.Provider>
  );
});

export default Composer;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  colorGray: {
    color: kColors.gray500,
  },
  composer: {
    borderColor: kColors.grayPink,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    flex: 1,
    justifyContent: "space-between",
    marginHorizontal: kStyleConst.kSpacing * 8,
    maxHeight: 200,
    minHeight: 60,
  },
  composerContainer: {
    borderColor: kColors.grayPink,
    borderTopWidth: 1,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  composerTextInput: {
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "300",
    height: "100%",
    letterSpacing: 0.8,
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  helperText: {
    bottom: -16,
    position: "absolute",
    right: 0,
  },
  m1: {
    margin: kStyleConst.kSpacing,
  },
  messageFile: {
    maxWidth: 150,
  },
  mobileComposer: {
    borderColor: kColors.grayPink,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    flex: 1,
    justifyContent: "space-between",
    marginHorizontal: kStyleConst.kSpacing * 5,
    maxHeight: 200,
    minHeight: 60,
  },
  mx10: {
    marginHorizontal: kStyleConst.kSpacing * 10,
  },
  mx5: {
    marginHorizontal: kStyleConst.kSpacing * 5,
  },
  p2: {
    padding: kStyleConst.kSpacing * 2,
  },
  px1: {
    paddingHorizontal: kStyleConst.kSpacing,
  },
  removeButton: {
    padding: 3 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  removeButtonIcon: {
    height: 7,
    width: 7,
  },
  textMedium: {
    fontWeight: "500",
  },
  textXxs: {
    fontSize: 10,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  editable: {
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    padding: 10,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: 300,
    letterSpacing: 0.8,
  },
};
